import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {
  FilterModel,
  FilterModels
} from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-filter.types';
import { MultiselectItem } from '@ui/aos-lib';
import { translationKeysMap } from 'src/app/services/translate/translate.key-mapper';
import { TranslateService } from 'src/app/services/translate/translate.service';
import {
  ChartDataPoint,
  ElectricalTableColumnDef,
  ElectricalTableData,
  ElectricalTableRow,
  GetChartDataPayload,
  GetChartDataResponse,
  GetFiltersPayload,
  GetFiltersResponse,
  GetTableDataPayload,
  GetTableDataResponse,
  MeterElectricalFieldName,
  TableMetric
} from './meter-electrical-tab-content.types';
import { DeviceHealthType } from 'src/app/pages/equipment/health-monitor/health-api.types';
import { DeviceHealthPresentation } from '../../device-details-metrics.types';
import { TimezoneService } from 'src/app/services/utility/timezone.service';

const PHASE_TO_CURRENT_TYPE: Record<string, DeviceHealthType> = {
  phaseA: DeviceHealthType.ElectricalCurrentPhaseA,
  phaseB: DeviceHealthType.ElectricalCurrentPhaseB,
  phaseC: DeviceHealthType.ElectricalCurrentPhaseC,
  phaseN: DeviceHealthType.ElectricalCurrentPhaseN
};

const PHASE_TO_VOLTAGE_TYPE: Record<string, DeviceHealthType> = {
  phaseA: DeviceHealthType.ElectricalVoltagePhaseA,
  phaseB: DeviceHealthType.ElectricalVoltagePhaseB,
  phaseC: DeviceHealthType.ElectricalVoltagePhaseC,
  phaseN: DeviceHealthType.ElectricalVoltagePhaseN
};

const KIND_ORDER = ['current', 'voltage-rms'];
const MEASURING_PERIOD_ORDER = [
  'twentyfourHour',
  'sixtyMinute',
  'thirtyMinute',
  'fifteenMinute',
  'fiveMinute',
  'oneMinute'
];
const PHASE_ORDER = ['phaseA', 'phaseB', 'phaseC', 'phaseN'];
const AGGREGATION_ORDER = ['none', 'maximum', 'minimum', 'average'];

@Injectable({ providedIn: 'root' })
export class MeterElectricalTabContentService {
  private readonly translate = inject(TranslateService);
  readonly #httpClient = inject(HttpClient);
  readonly #timeZoneService = inject(TimezoneService);

  mapChartDataResponse(response: GetChartDataResponse): DeviceHealthPresentation[] {
    return [
      ...this.#groupByPhase(response.current, PHASE_TO_CURRENT_TYPE),
      ...this.#groupByPhase(response.voltage, PHASE_TO_VOLTAGE_TYPE)
    ];
  }

  #groupByPhase(items: ChartDataPoint[], phaseTypeMap: Record<string, DeviceHealthType>): DeviceHealthPresentation[] {
    const groups = new Map<string, ChartDataPoint[]>();
    for (const item of items) {
      if (!groups.has(item.phase)) groups.set(item.phase, []);
      groups.get(item.phase)!.push(item);
    }
    return [...groups.entries()]
      .filter(([phase]) => phaseTypeMap[phase])
      .map(([phase, points]) => {
        const type = phaseTypeMap[phase];
        return {
          type,
          health: {
            type,
            data: points.map(p => ({ x: p.date, y: p.value })),
            unitOfMeasure: points[0]?.unitOfMeasure?.toUpperCase() ?? '',
            multiplier: points[0]?.multiplier ?? ''
          },
          config: []
        };
      });
  }

  getChartData(payload: GetChartDataPayload): Observable<GetChartDataResponse> {
    return this.#httpClient.post<GetChartDataResponse>('/api/device-electric-metric-chart', payload);
  }

  getTableData(payload: GetTableDataPayload): Observable<GetTableDataResponse> {
    return this.#httpClient.post<GetTableDataResponse>('/api/device-electric-metric-list', payload);
  }

  mapTableDataResponse(response: GetTableDataResponse): ElectricalTableData {
    const seen = new Map<string, TableMetric>();

    for (const item of response.items) {
      for (const metric of item.metrics) {
        const key = `${metric.aggregate}_${metric.kind}_${metric.phase}`;
        if (!seen.has(key)) {
          seen.set(key, metric);
        }
      }
    }

    const columnDefs: ElectricalTableColumnDef[] = [...seen.entries()].map(([key, metric]) => ({
      key,
      title: this.#buildColumnTitle(metric),
      csvTitle: this.#buildCsvColumnTitle(metric)
    }));

    const data: ElectricalTableRow[] = response.items.map(item => {
      const row: ElectricalTableRow = {
        dateTime: item.dateTime ? this.#timeZoneService.formatLong(item.dateTime) : item.dateTime
      };
      for (const metric of item.metrics) {
        row[`${metric.aggregate}_${metric.kind}_${metric.phase}`] = metric.value;
      }
      return row;
    });

    return { columnDefs, data, total: response.totalCount };
  }

  #buildColumnTitle(metric: TableMetric): string {
    const aggregateLabel = this.#translateValue(metric.aggregate);
    const kindLabel = this.#translateValue(metric.kind);
    const phaseLabel = this.#translateValue(metric.phase);
    const multiplierPart = metric.multiplier && metric.multiplier.toLowerCase() !== 'none' ? metric.multiplier : '';
    const uomSuffix = multiplierPart || metric.unitOfMeasure ? ` (${multiplierPart}${metric.unitOfMeasure})` : '';
    return `${aggregateLabel} ${kindLabel} ${phaseLabel}${uomSuffix}`;
  }

  #buildCsvColumnTitle(metric: TableMetric): string {
    const aggregateLabel = this.#translateValue(metric.aggregate);
    const kindLabel = this.#translateValue(metric.kind);
    const phaseLabel = this.#translateValue(metric.phase);
    const multiplierPart = metric.multiplier && metric.multiplier.toLowerCase() !== 'none' ? metric.multiplier : '';
    const uomSuffix = multiplierPart || metric.unitOfMeasure ? `(${multiplierPart}${metric.unitOfMeasure})` : '';
    return `${aggregateLabel}${kindLabel}${phaseLabel}${uomSuffix}`;
  }

  #translateValue(value: string): string {
    const key = translationKeysMap.get(value);
    return key ? this.translate.instant(key) : value;
  }

  getFilters(payload: GetFiltersPayload): Observable<FilterModels> {
    return this.#httpClient.post<GetFiltersResponse>('/api/device-electric-metric-filters', payload).pipe(
      map(response => [
        this.#createKindFilter(response.kinds ?? []),
        this.#createIntervalFilter(response.measuringPeriods ?? []),
        this.#createPhaseFilter(response.phases ?? []),
        this.#createAggregationFilter(response.aggregations ?? [])
      ]),
      catchError(error => {
        console.error(error);
        return of([]);
      })
    );
  }

  #createKindFilter(kinds: string[]): FilterModel {
    const items = this.#toItems(kinds, KIND_ORDER);
    return {
      filterType: MeterElectricalFieldName.metric,
      title: 'Kind',
      isMultiTag: true,
      width: 160,
      items: this.translate.getTranslatedItems(items),
      selected: this.translate.getTranslatedItems(items)
    };
  }

  #createIntervalFilter(measuringPeriods: string[]): FilterModel {
    const items = this.#toItems(measuringPeriods, MEASURING_PERIOD_ORDER);
    const selected = items.length ? [items[0]] : [];
    return {
      filterType: MeterElectricalFieldName.interval,
      title: 'Interval',
      isMultiTag: false,
      width: 112,
      items: this.translate.getTranslatedItems(items),
      selected: this.translate.getTranslatedItems(selected)
    };
  }

  #createPhaseFilter(phases: string[]): FilterModel {
    const items = this.#toItems(phases, PHASE_ORDER);
    return {
      filterType: MeterElectricalFieldName.phase,
      title: 'Phase',
      isMultiTag: true,
      width: 160,
      items: this.translate.getTranslatedItems(items),
      selected: this.translate.getTranslatedItems(items)
    };
  }

  #createAggregationFilter(aggregations: string[]): FilterModel {
    const items = this.#toItems(aggregations, AGGREGATION_ORDER);
    const maxItem = items.find(item => item.value === 'maximum');
    const selected = maxItem ? [maxItem] : items.length ? [items[0]] : [];
    return {
      filterType: MeterElectricalFieldName.aggregation,
      title: 'Aggregation',
      isMultiTag: false,
      width: 112,
      items: this.translate.getTranslatedItems(items),
      selected: this.translate.getTranslatedItems(selected)
    };
  }

  #toItems(values: string[], order: string[]): MultiselectItem[] {
    return [...values]
      .sort((a, b) => {
        const ai = order.indexOf(a);
        const bi = order.indexOf(b);
        return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
      })
      .map(item => ({ text: translationKeysMap.get(item) || item, value: item }));
  }
}
